import { FC, ReactNode } from "react";
import { Contract } from "@models/contract.model";
import { Budget } from "@models/budget.model";
import { Pricesheet } from "@models/pricesheet.model";
import { PricesheetItem } from "@models/pricesheetitem.model";
import { SpendAccount } from "@models/spendaccount.model";
import { Credit } from "@models/credit.model";
import { Debit } from "@models/debit.model";
import { UpdateContractPayload } from "@services/finance/contracts/commands/cmdUpdateContract";
import { BILLING_MODEL_LABELS, PRODUCT_OPTIONS } from "../types";
import ContractDetailsSection from "./panels/ContractDetailsSection";
import ContractBudgetsPanel from "./panels/ContractBudgetsPanel";
import ContractPricesheetsPanel from "./panels/ContractPricesheetsPanel";
import ContractSpendPanel, { SpendHandlers } from "./panels/ContractSpendPanel";

const PRODUCT_LABELS: Record<string, string> = Object.fromEntries(
  PRODUCT_OPTIONS.map((p) => [p.value, p.label]),
);

export interface ContractDetailHandlers extends SpendHandlers {
  onEditContract?: (contract: Contract) => void;
  onDeleteContract?: (contract: Contract) => void;
  /** Enables inline click-to-edit of simple contract fields. */
  onUpdateContract?: (payload: UpdateContractPayload) => void | Promise<void>;
  onCreateBudget?: () => void;
  onEditBudget?: (budget: Budget) => void;
  onDeleteBudget?: (budget: Budget) => void;
  onCreatePricesheet?: () => void;
  onEditPricesheet?: (pricesheet: Pricesheet) => void;
  onDeletePricesheet?: (pricesheet: Pricesheet) => void;
}

interface Props extends ContractDetailHandlers {
  contract: Contract;
  budgets: readonly Budget[];
  pricesheets: readonly Pricesheet[];
  pricesheetItems: readonly PricesheetItem[];
  spendAccount?: SpendAccount | null;
  credits?: readonly Credit[];
  debits?: readonly Debit[];
  balance?: number | null;
  /** Header title — defaults to "Contract #{id}". */
  title?: string;
  /** Right-aligned status (e.g., "Assigned" badge for engagement view). */
  statusSlot?: ReactNode;
  /** Right-aligned action slot (e.g., remove button or "manage" link). */
  actionsSlot?: ReactNode;
}

function Section({
  title,
  hint,
  children,
}: {
  title: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="contract-canvas-card">
      <div className="contract-canvas-card__head">
        <h3 className="contract-canvas-card__title">{title}</h3>
        {hint && <span className="contract-canvas-card__hint">{hint}</span>}
      </div>
      <div className="contract-canvas-card__body">{children}</div>
    </div>
  );
}

const ContractDetailCard: FC<Props> = ({
  contract,
  budgets,
  pricesheets,
  pricesheetItems,
  spendAccount = null,
  credits = [],
  debits = [],
  balance = null,
  title,
  statusSlot,
  actionsSlot,
  onEditContract,
  onDeleteContract,
  onUpdateContract,
  onCreateBudget,
  onEditBudget,
  onDeleteBudget,
  onCreatePricesheet,
  onEditPricesheet,
  onDeletePricesheet,
  onAddCredit,
  onAddDebit,
  onEditCredit,
  onDeleteCredit,
  onEditDebit,
  onDeleteDebit,
}) => {
  const showContractActions = !!(onEditContract || onDeleteContract);

  const subtitleParts = [
    contract.billing_method
      ? BILLING_MODEL_LABELS[contract.billing_method] ?? contract.billing_method
      : null,
    contract.contract_start_date
      ? `${contract.contract_start_date} → ${contract.contract_end_date ?? "ongoing"}`
      : null,
    contract.products.length
      ? contract.products.map((p) => PRODUCT_LABELS[p] ?? p).join(", ")
      : null,
  ].filter(Boolean);

  return (
    <div className="contract-canvas">
      {/* Hero */}
      <div className="contract-canvas-hero">
        <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap">
          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <i className="bi bi-file-earmark-check" style={{ color: "#667eea" }} />
              <span className="contract-canvas-hero__title">
                {title ?? `Contract #${contract.id}`}
              </span>
              <span
                className={`contract-status-badge ${contract.is_active ? "active" : "inactive"}`}
              >
                {contract.is_active ? "Active" : "Inactive"}
              </span>
              {statusSlot}
            </div>
            {subtitleParts.length > 0 && (
              <div className="contract-canvas-hero__sub">{subtitleParts.join(" · ")}</div>
            )}
          </div>
          <div className="d-flex gap-2 align-items-center">
            {showContractActions && (
              <>
                {onEditContract && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => onEditContract(contract)}
                    title="Edit contract"
                  >
                    <i className="bi bi-pencil" />
                  </button>
                )}
                {onDeleteContract && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => onDeleteContract(contract)}
                    title="Delete contract"
                  >
                    <i className="bi bi-trash" />
                  </button>
                )}
              </>
            )}
            {actionsSlot}
          </div>
        </div>
      </div>

      <Section
        title="Contract details"
        hint={onUpdateContract ? "click a value to edit" : undefined}
      >
        <ContractDetailsSection contract={contract} onUpdateContract={onUpdateContract} />
      </Section>

      <Section title={`Budgets (${budgets.length})`}>
        <ContractBudgetsPanel
          budgets={budgets}
          onCreate={onCreateBudget}
          onEdit={onEditBudget}
          onDelete={onDeleteBudget}
        />
      </Section>

      <Section title={`Pricesheets (${pricesheets.length})`}>
        <ContractPricesheetsPanel
          pricesheets={pricesheets}
          items={pricesheetItems}
          onCreate={onCreatePricesheet}
          onEdit={onEditPricesheet}
          onDelete={onDeletePricesheet}
        />
      </Section>

      <Section title="Spend account">
        <ContractSpendPanel
          spendAccount={spendAccount}
          credits={credits}
          debits={debits}
          balance={balance}
          onAddCredit={onAddCredit}
          onAddDebit={onAddDebit}
          onEditCredit={onEditCredit}
          onDeleteCredit={onDeleteCredit}
          onEditDebit={onEditDebit}
          onDeleteDebit={onDeleteDebit}
        />
      </Section>
    </div>
  );
};

export default ContractDetailCard;
